import { useTranslation } from 'react-i18next';
import { Heading, Text } from '@bn-apps/design-system-ui-react';
import { Container, PageHeader } from '@bn-apps/dim-ui-react';
import { useKeyFigures } from '@hooks/useSparql';
import { useAvailableViewportHeight } from '@hooks/useAvailableViewportHeight';
import ScalarTable from '@components/keyFigures/ScalarTable';
import DataTable from '@components/keyFigures/DataTable';
import LoadingState from '@components/common/LoadingState/LoadingState';
import ScrollToTopButton from '@components/common/ScrollToTopButton';
import appStyles from '@/App.module.scss';
import styles from './KeyFiguresPage.module.scss';

/** Extract the local name from a full URI for use as an HTML id. */
function toId(uri: string): string {
  const local = uri.split(/[#/]/).filter(Boolean).pop() ?? uri;
  return local.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function KeyFiguresPage(): JSX.Element {
  const { t } = useTranslation();
  const { data: sections, loading, error } = useKeyFigures();
  const sidebarRef = useAvailableViewportHeight<HTMLElement>({
    bottomOffset: 24,
  });

  const tocNav = !loading && !error && sections.length > 0 ? (
    <nav aria-label={t('keyFigures.toc.ariaLabel')} className={styles.toc}>
      <Heading level={2} fontSize="sm" className={styles.tocTitle}>
        {t('keyFigures.toc.title')}
      </Heading>
      <ol className={styles.tocList}>
        {sections.map(section => (
          <li key={section.uri} className={styles.tocSectionItem}>
            <a href={`#${toId(section.uri)}`} className={styles.tocSectionLink}>
              {section.title}
            </a>
            {section.tables.length > 0 && (
              <ol className={styles.tocTableList}>
                {section.tables.map(table => (
                  <li key={table.uri}>
                    <a href={`#${toId(table.uri)}`} className={styles.tocTableLink}>
                      {table.title}
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
    </nav>
  ) : null;

  return (
    <>
      <Container className={appStyles.pageContent}>
      <div className="bn-mbe-1000">
        <PageHeader title={t('keyFigures.title')} lead={t('keyFigures.description')} />
      </div>

      {tocNav && (
        <div className={styles.inlineToc}>
          {tocNav}
        </div>
      )}

      <div className={styles.pageLayout}>
        {tocNav && (
          <aside className={styles.sidebar} ref={sidebarRef}>
            {tocNav}
          </aside>
        )}

        <main className={styles.mainContent}>
          {loading && (
            <LoadingState />
          )}

          {!loading && error && (
            <Text fontSize="md" className={styles.errorText}>
              {t('keyFigures.error')}
            </Text>
          )}

          {!loading && !error && sections.length === 0 && (
            <Text fontSize="md" className={styles.emptyText}>
              {t('keyFigures.empty.noSections')}
            </Text>
          )}

          {!loading && !error && sections.map(section => (
            <section
              key={section.uri}
              id={toId(section.uri)}
              aria-labelledby={`heading-${toId(section.uri)}`}
              className={styles.section}
            >
              <Heading
                level={2}
                fontSize="xl"
                id={`heading-${toId(section.uri)}`}
                className={styles.sectionHeading}
              >
                {section.title}
              </Heading>

              {section.scalars.length > 0 && (
                <div className={styles.scalarWrapper}>
                  <ScalarTable scalars={section.scalars} />
                </div>
              )}

              {section.tables.map(table => (
                <DataTable key={table.uri} id={toId(table.uri)} table={table} />
              ))}
            </section>
          ))}
        </main>
      </div>
      </Container>
      <ScrollToTopButton />
    </>
  );
}

export default KeyFiguresPage;
